import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useBuildingInPublic } from '../hooks/useAPI';
import { XLogo } from './XPost';
import XPostCard from './XPostCard';
import XPostModal from './XPostModal';

// Carrossel infinito com os posts do building in public; clique abre o post
// inteiro no XPostModal. A fonte é a mesma da timeline em
// adverse.com.br/building-in-public: post novo importado lá aparece aqui no
// próximo deploy do adverse-start, sem mexer neste repo.

const TIMELINE_URL = 'https://www.adverse.com.br/building-in-public';
const PROFILE_URL = 'https://x.com/joaobrocatto';

// Metade do trilho precisa ser mais larga que a tela, senão aparece buraco
// antes do loop fechar: 8 cards de 296px já passam de 2.300px.
const MIN_CARDS = 8;
const SECONDS_PER_CARD = 7;

const SkeletonRow = () => (
  <div className="flex gap-4 overflow-hidden px-4 py-2" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="flex h-[340px] w-[280px] shrink-0 animate-pulse flex-col rounded-2xl border border-white/10 bg-dark-card/70 p-4 sm:w-[340px]">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-white/10" />
          <div className="h-3 w-1/2 rounded bg-white/10" />
        </div>
        <div className="mt-4 space-y-2">
          <div className="h-3 w-full rounded bg-white/10" />
          <div className="h-3 w-5/6 rounded bg-white/10" />
          <div className="h-3 w-2/3 rounded bg-white/10" />
        </div>
        <div className="mt-auto aspect-video rounded-xl bg-white/5" />
      </div>
    ))}
  </div>
);

const OpenStartupLog = () => {
  const { items, loading, error } = useBuildingInPublic();
  const [selected, setSelected] = useState(null);
  const closeModal = useCallback(() => setSelected(null), []);

  const half = useMemo(() => {
    if (!items.length) return [];
    const out = [];
    while (out.length < MIN_CARDS) out.push(...items);
    return out;
  }, [items]);
  const track = [...half, ...half];

  return (
    <section id="open-startup" className="py-20">
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-3xl md:text-4xl font-display font-bold mb-12 section-title"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          ?OPEN STARTUP LOG
        </motion.h2>

        <motion.div
          className="mb-8 flex flex-col gap-5 md:flex-row md:items-center md:justify-between"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-primary mr-3 shrink-0" />
            <h3 className="text-xl font-display font-bold">Números reais dos negócios (ou de MVPs que esteja testando).</h3>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-3">
            <a
              href={TIMELINE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-x-blue hover:underline"
            >
              Linha do tempo completa
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
            <a
              href={PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-light-text px-4 py-1.5 text-sm font-bold text-dark transition-opacity hover:opacity-90"
            >
              <XLogo className="h-4 w-4" />
              Seguir @joaobrocatto
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        viewport={{ once: true }}
      >
        {loading && <SkeletonRow />}

        {!loading && (error || items.length === 0) && (
          <div className="container mx-auto px-4">
            <div className="glassmorphism rounded-xl px-5 py-10 text-center">
              <p className="text-secondary-text mb-4">Não deu pra carregar os posts agora.</p>
              <a href={TIMELINE_URL} target="_blank" rel="noopener noreferrer" className="text-x-blue hover:underline">
                Ver os updates em adverse.com.br
              </a>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          // py-2: o overflow do carrossel cortaria o anel de foco dos cards
          <div className="marquee py-2" data-paused={selected ? 'true' : 'false'}>
            <div className="marquee-track" style={{ '--marquee-duration': `${half.length * SECONDS_PER_CARD}s` }}>
              {track.map((item, i) => {
                const dup = i >= items.length;
                return (
                  <div key={`${item.id}-${i}`} className={`shrink-0 pr-4 ${dup ? 'marquee-dup' : ''}`}>
                    <XPostCard item={item} onOpen={setSelected} hidden={dup} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>

      <XPostModal item={selected} onClose={closeModal} />
    </section>
  );
};

export default OpenStartupLog;
